import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { currentCalendarMonth } from "../domain/reconciliation";
import type { ProfitMode } from "../shared/profit-types";
import type { Bootstrap } from "./types";

type AppContextValue = {
  bootstrap: Bootstrap;
  storeId: string;
  platformFilter: "" | "amazon" | "tiktok";
  setPlatformFilter(value: "" | "amazon" | "tiktok"): void;
  siteFilter: string;
  setSiteFilter(value: string): void;
  month: string;
  setStoreId(value: string): void;
  setMonth(value: string): void;
  periodGranularity: "day" | "week" | "month" | "quarter" | "year";
  setPeriodGranularity(value: "day" | "week" | "month" | "quarter" | "year"): void;
  periodAnchor: string;
  setPeriodAnchor(value: string): void;
  reportingCurrency: "CNY" | "USD";
  setReportingCurrency(value: "CNY" | "USD"): void;
  profitMode: ProfitMode;
  setProfitMode(value: ProfitMode): void;
  unsavedChanges: boolean;
  setUnsavedChanges(value: boolean): void;
  confirmDiscardChanges(): boolean;
  refreshBootstrap(): Promise<void>;
};

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ bootstrap, refreshBootstrap, children }: { bootstrap: Bootstrap; refreshBootstrap(): Promise<void>; children: ReactNode }) {
  const [storeId, setStoreId] = useState("");
  const [platformFilter, setPlatformFilter] = useState<"" | "amazon" | "tiktok">("");
  const [siteFilter, setSiteFilter] = useState("");
  const [month, setMonth] = useState(currentCalendarMonth());
  const [periodGranularity, setPeriodGranularity] = useState<"day" | "week" | "month" | "quarter" | "year">("month");
  const [periodAnchor, setPeriodAnchor] = useState(`${currentCalendarMonth()}-01`);
  const [reportingCurrency, setReportingCurrency] = useState<"CNY" | "USD">("CNY");
  const [profitMode, setProfitMode] = useState<ProfitMode>("estimated");
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    if (storeId && !bootstrap.stores.some((store) => store.id === storeId)) setStoreId("");
  }, [bootstrap.stores, storeId]);

  useEffect(() => {
    if (!unsavedChanges) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [unsavedChanges]);

  function confirmDiscardChanges() {
    return !unsavedChanges || window.confirm("当前有未保存的内容，确定放弃并离开吗？");
  }

  const value = useMemo(
    () => ({ bootstrap, storeId, platformFilter, setPlatformFilter, siteFilter, setSiteFilter, month, setStoreId, setMonth, periodGranularity, setPeriodGranularity, periodAnchor, setPeriodAnchor, reportingCurrency, setReportingCurrency, profitMode, setProfitMode, unsavedChanges, setUnsavedChanges, confirmDiscardChanges, refreshBootstrap }),
    [bootstrap, storeId, platformFilter, siteFilter, month, periodGranularity, periodAnchor, reportingCurrency, profitMode, unsavedChanges, refreshBootstrap]
  );
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const value = useContext(AppContext);
  if (!value) throw new Error("AppProvider 未初始化");
  return value;
}
